import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import type { CustomEmberTableColumn } from '@mylighthouse/prism-ember/types/components/prism/table';
import TooltipRoomsMonth from 'lumen/components/hotel/pricing-assistant/table/tooltips/tooltip-rooms-month';
import type { Carate, Period } from 'lumen/models';

export interface RoomsMonthColumn extends CustomEmberTableColumn {
  rooms: Carate[];
}

interface HotelPricingAssistantTableCellsTdRoomsMonthSignature {
  Args: {
    column: RoomsMonthColumn;
    value: Period;
  };
}

export default class HotelPricingAssistantTableCellsTdRoomsMonth extends Component<HotelPricingAssistantTableCellsTdRoomsMonthSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service('pricing-assistant-data-request-service')
  declare pricingAssistantDataRequestService: Services['pricing-assistant-data-request-service'];

  /* As the Rate groups cell: the count of rooms restricted by MinLOS; "Open" when
     every known value is unrestricted; "--" when no value is known. */
  get displayValue(): string {
    const restrictedCount = this.minLosValues.filter(
      (value) => value != null && value > 0,
    ).length;

    if (restrictedCount > 0) {
      return this.intl.formatNumber(restrictedCount);
    }

    if (!this.isMissing) {
      return this.intl.t('channel_pricing.restriction.none');
    }

    return this.intl.t('general.missing');
  }

  /* Until both requests land, `roomMinLos` reads a stale or partial value. */
  get isLoading(): boolean {
    const { restrictionsRecommendationsRequestTask, restrictionsRequestTask } =
      this.pricingAssistantDataRequestService;

    return (
      restrictionsRequestTask.isRunning ||
      restrictionsRecommendationsRequestTask.isRunning
    );
  }

  /* With no known value the hovercard would only repeat "--", so the cell has
     no hovercard and no tab stop. */
  get isMissing(): boolean {
    return this.minLosValues.every((value) => value == null);
  }

  get isPast(): boolean {
    return this.hotelAndMarketService.isPast(this.args.value.fromDate);
  }

  get minLosValues(): (number | null)[] {
    return this.args.column.rooms.map(({ pmsRoomtype }) =>
      this.args.value.roomMinLos(pmsRoomtype),
    );
  }

  <template>
    {{#if this.isLoading}}
      <div class="loading-pulse" data-test-id="td-rooms-month-loading"></div>
    {{else}}
      <div
        class="mr-500
          {{if
            this.isPast
            'text-disabled'
            (if this.isMissing 'text-neutral-subdued')
          }}"
        data-test-id="td-rooms-month"
        tabindex={{unless this.isMissing "0"}}
      >
        {{this.displayValue}}
        {{#unless this.isMissing}}
          <TooltipRoomsMonth @period={{@value}} @rooms={{@column.rooms}} />
        {{/unless}}
      </div>
    {{/if}}
  </template>
}
